import React from 'react';
import { Shield, Lock, Eye, Database, Users, Mail } from 'lucide-react';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-3 mb-6">
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-green-300/50 shadow-sm">
            <Shield className="w-10 h-10 text-blue-700" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-black">
            Privacy Policy
          </h2>
        </div>
        <p className="text-gray-700 text-lg max-w-2xl mx-auto">
          How we collect, use, and protect your information
        </p>
      </div>

      <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-2xl p-8 shadow-lg">
        <div className="prose prose-lg max-w-none">
          <div className="text-sm text-gray-600 mb-8">
            <p><strong>Last updated:</strong> January 14, 2026</p>
          </div>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Lock className="w-6 h-6 text-green-600 mr-2" />
              Introduction
            </h3>
            <p className="text-gray-700 mb-4">
              CommsGenie ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our legal document management service.
            </p>
            <p className="text-gray-700">
              By using CommsGenie, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Eye className="w-6 h-6 text-green-600 mr-2" />
              Information We Collect
            </h3>
            
            <h4 className="text-xl font-semibold text-black mb-3">Personal Information</h4>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Email address and account credentials</li>
              <li>Name and organization information</li>
              <li>Usage data and preferences</li>
              <li>Communication records with our support team</li>
            </ul>

            <h4 className="text-xl font-semibold text-black mb-3">Document Information</h4>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>FCC docket numbers and search queries</li>
              <li>Uploaded documents and files</li>
              <li>Generated summaries and analyses</li>
              <li>Bookmarks and saved searches</li>
            </ul>

            <h4 className="text-xl font-semibold text-black mb-3">Technical Information</h4>
            <ul className="list-disc pl-6 text-gray-700">
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Usage patterns and analytics data</li>
              <li>Error logs and performance metrics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Database className="w-6 h-6 text-green-600 mr-2" />
              How We Use Your Information
            </h3>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Provide and maintain our legal document management services</li>
              <li>Process FCC docket searches and document downloads</li>
              <li>Generate AI-powered summaries and analyses</li>
              <li>Improve our services and user experience</li>
              <li>Send important notifications and updates</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">AI and Machine Learning</h3>
            <p className="text-gray-700 mb-4">
              We use artificial intelligence and machine learning technologies to provide our document analysis and summarization services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>AI models process your uploaded documents to generate summaries and insights</li>
              <li>Document content is sent to third-party AI providers (OpenAI) for processing</li>
              <li>We do not use your documents to train AI models without your explicit consent</li>
              <li>AI-generated content should be reviewed by qualified professionals before use</li>
            </ul>
            <p className="text-gray-700">
              You acknowledge that AI-generated summaries are tools to assist your work and should not be relied upon as legal advice.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Users className="w-6 h-6 text-green-600 mr-2" />
              Information Sharing and Disclosure
            </h3>
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
            </p>
            
            <h4 className="text-xl font-semibold text-black mb-3">Service Providers</h4>
            <p className="text-gray-700 mb-3">
              We work with trusted third-party service providers who assist us in operating our service:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Supabase (database and authentication services)</li>
              <li>OpenAI (AI-powered document analysis)</li>
              <li>Stripe (payment processing)</li>
              <li>Nylas (email notifications)</li>
              <li>Sentry (error monitoring and application performance)</li>
              <li>Pinecone (vector storage for document search)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              These providers have access only to the information necessary to perform their functions and are contractually obligated to protect your data.
            </p>

            <h4 className="text-xl font-semibold text-black mb-3">Other Disclosures</h4>
            <ul className="list-disc pl-6 text-gray-700">
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, your information may be transferred</li>
              <li><strong>Consent:</strong> We may share information with your explicit consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Shield className="w-6 h-6 text-green-600 mr-2" />
              Data Security
            </h3>
            <p className="text-gray-700 mb-4">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Encryption of data in transit using industry-standard TLS/HTTPS protocols</li>
              <li>Encryption of data at rest in secure cloud databases</li>
              <li>Payment processing handled by Stripe, a PCI DSS compliant payment processor (we do not store credit card information)</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">Your Rights and Choices</h3>
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Account Deletion:</strong> Delete your account and associated data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">Data Retention</h3>
            <p className="text-gray-700 mb-4">
              We retain your information for as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Account information: Until account deletion</li>
              <li>Usage data: Up to 2 years for analytics and improvement</li>
              <li>Uploaded documents and analyses: Retained while your account is active and for up to 30 days after account deletion (unless you request immediate deletion)</li>
              <li>Communication records: Up to 3 years for support purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">Cookies and Tracking</h3>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li><strong>Essential Cookies:</strong> Required for basic functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Security Cookies:</strong> Protect against fraud and abuse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">Children's Privacy</h3>
            <p className="text-gray-700">
              Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">International Data Transfers</h3>
            <p className="text-gray-700 mb-4">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Our primary data processing occurs in the United States</li>
              <li>We use service providers that may process data in multiple regions including the US and EU</li>
              <li>All international transfers comply with applicable data protection frameworks</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4">Changes to This Policy</h3>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-black mb-4 flex items-center">
              <Mail className="w-6 h-6 text-green-600 mr-2" />
              Contact Us
            </h3>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-700">
                <strong>Email:</strong> privacy@commsgenie.com
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
